<script setup lang="ts">
import { computed, onMounted, type Ref, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import MagnifyingGlass from '@/assets/icons/MagnifyingGlass.vue';
import { Address } from '@/models/Address';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import { type AddressInputStatus } from '@/components/session/kyc/address/address.types';
import { useDebounceFn, whenever } from '@vueuse/core';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';
import { ImmersveError } from '@/common/ImmersveError';

const addressInputStatus: Ref<AddressInputStatus> = ref('Input');
const model = defineModel({
  type: Address,
  required: true,
});

const props = defineProps({
  regionOverride: {
    type: String,
    required: true,
  },
  switchMode: {
    type: Function,
    required: true,
  },
  saveAddress: {
    type: Function,
    required: true,
  },
});

const buttonText = computed(() => (addressInputStatus.value === 'Checking' ? 'Checking' : 'Next'));
const autocomplete = ref<google.maps.places.Autocomplete | undefined>(undefined);

const isNextEnabled = ref(false);
const isValidationVisible = ref(false);
const addressInput = ref<HTMLInputElement | null>(null);
const skipNextFullAddressWatch = ref(false);

const fullAddressString = ref(model.value?.fullAddress);

whenever(
  () => isValidationVisible.value,
  () => Mixpanel.instance.trackAddressValidationFail()
);

const onFullAddressStringUpdate = useDebounceFn(async (newFullAddressString: string) => {
  if (newFullAddressString) {
    Mixpanel.instance.trackVerifiableAddressEntered();
  }
  await checkData(newFullAddressString);
}, 500);

watch(
  () => fullAddressString.value ?? '',
  async (newFullAddressString: string) => {
    if (skipNextFullAddressWatch.value) {
      skipNextFullAddressWatch.value = false;
      return;
    }

    await onFullAddressStringUpdate(newFullAddressString);
  }
);

// eslint-disable-next-line complexity
async function checkData(newFullAddressString: string) {
  isValidationVisible.value = false;
  isNextEnabled.value = false;

  try {
    if (!newFullAddressString) {
      //handling on input change
      return;
    }

    addressInputStatus.value = 'Checking';

    const newAddress = await Address.tryParseAddressString(
      newFullAddressString,
      props.regionOverride
    );

    //address has all the parts and it is the same as the previous one
    if (!newAddress.missingParts && newAddress.address.fullAddress === model.value?.fullAddress) {
      model.value = newAddress.address;
      isNextEnabled.value = true;
      isValidationVisible.value = false;
      return;
    }

    const isMissingStreetNumber = newAddress.missingParts?.includes('street_number');
    const justMissingStreetNumber = newAddress.missingParts?.length === 1 && isMissingStreetNumber;

    //if it's just missing the street number we're good to model bind.
    if (!justMissingStreetNumber && !newAddress.hasAllParts) {
      isValidationVisible.value = true;
      return;
    }

    skipNextFullAddressWatch.value = true;
    fullAddressString.value = newAddress.address.fullAddress;

    model.value = newAddress.address;

    // we're going to update the model, but we're not saying this is a valid address.
    if (justMissingStreetNumber) {
      isValidationVisible.value = true;
      return;
    }
    isNextEnabled.value = true;
    isValidationVisible.value = false;
    Mixpanel.instance.trackAddressValidationSuccess();
  } catch (e) {
    if (e instanceof ImmersveError && e.name === 'GoogleAddressValidateTooLong') {
      isValidationVisible.value = true;
      return;
    }
  } finally {
    addressInputStatus.value = 'Saving';
  }
}

function addressSelectionChanged() {
  try {
    if (!autocomplete.value) {
      return;
    }
    const place = autocomplete.value.getPlace();
    if (place?.formatted_address) {
      fullAddressString.value = place.formatted_address;
    }
  } catch (e) {
    Sentry.captureException(e);
  }
}

function onInput(event: Event) {
  isValidationVisible.value = false;
  isNextEnabled.value = false;
  const input = event.target as HTMLInputElement;

  //handle deletion
  if (input.value) {
    model.value = Address.create();
  }
}

async function onSaveAddress() {
  await props.saveAddress();

  return true;
}

onMounted(() => {
  try {
    if (!addressInput.value) {
      return;
    }
    autocomplete.value = new google.maps.places.Autocomplete(addressInput.value, {
      types: ['address'], //https://developers.google.com/maps/documentation/javascript/supported_types#table3
      fields: ['address_components', 'formatted_address'],
      strictBounds: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      locationbias: 'ipbias',
      componentRestrictions: { country: props.regionOverride },
    });

    autocomplete.value.addListener('place_changed', addressSelectionChanged);
  } catch (e) {
    Sentry.captureException(e);
  }
});

async function init() {
  await checkData(fullAddressString.value);
}

init();
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col">
      <h1 class="text-3xl">What is your current residential address?</h1>
      <div class="mt-4 mb-4">Your address cannot be a PO box</div>
      <div>
        <div class="flex flex-1 flex-col" data-test="screen-address-auto">
          <label for="address" class="text-light-primary mb-2 font-bold">Residential address</label>
          <div
            class="border-light-border text-light-primary dark:border-dark-brd-dark dark:bg-dark-list-bg dark:text-dark-primary relative h-12 rounded-md border font-normal"
          >
            <MagnifyingGlass class="absolute top-4 left-2" />
            <input
              name="address"
              id="address"
              type="text"
              data-test="address-input-auto"
              class="box-border flex h-full w-full bg-transparent pr-2 pl-7 text-base outline-none"
              placeholder="Start typing your address..."
              v-model.lazy="fullAddressString"
              @input="onInput"
              autocomplete="off"
              ref="addressInput"
            />
          </div>
        </div>
      </div>
      <p
        class="text-light-utility-red dark:text-dark-utility-red mt-1 py-0"
        v-if="isValidationVisible"
        data-test="address-validation-error"
      >
        <span
          >We could not validate this address. Please check the address and try again or enter the
          address manually.</span
        >
      </p>
      <a
        href="#"
        class="text-light-secondary dark:text-dark-border mt-2 mb-16 text-sm font-semibold underline"
        @click.prevent="props.switchMode()"
        data-test="change-mode"
      >
        <span>Enter address manually</span>
      </a>
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton
        :preNavigationAction="onSaveAddress"
        :text="buttonText"
        :disabled="!isNextEnabled"
        direction="forward"
      />
    </template>
  </KycSessionLayout>
</template>

<style>
@reference "../../../../style.css";
@reference "tailwindcss";

.pac-container {
  @apply border-light-border bg-light-list-bg dark:border-dark-brd-dark dark:bg-dark-list-bg w-full rounded-md border outline-none;
}

.pac-icon,
.hdpi.pac-logo::after,
.pac-logo::after {
  @apply hidden;
}

.pac-item {
  @apply text-light-primary dark:text-dark-primary border-none py-2 pl-2;
}

.pac-item-query,
.pac-matched {
  @apply text-light-primary dark:text-dark-primary;
}

.pac-item.pac-item-selected {
  @apply bg-light-select;
}

.pac-item.pac-item-selected,
.pac-item.pac-item-selected .pac-item-query,
.pac-item.pac-item-selected .pac-matched {
  @apply font-bold;
}

.pac-item:hover {
  @apply dark:bg-dark-list-bg dark:text-dark-border bg-slate-50;
}

.pac-item:hover .pac-item-query,
.pac-item:hover .pac-matched {
  @apply dark:text-dark-border;
}
</style>
